import React, { useEffect, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CardHeader,
  Divider,
} from "@mui/material";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

function TripChart() {
  const serviceType = useSelector((state) => state.serviceType.serviceType); // Get service type from Redux
  const tripData = useSelector((state) => state.trip?.data?.data); // Get trip data from Redux
  const [selectedYearMonth, setSelectedYearMonth] = useState(dayjs().format("YYYY-MM"));
  const [areaChartData, setAreaChartData] = useState([]);

  // Process tripData whenever it or selectedYearMonth changes
  useEffect(() => {
    // console.log("tripData",tripData)
    if (tripData?.length > 0) {
      processTripData();
    }
  }, [tripData, selectedYearMonth]);

  const processTripData = () => {
    const [year, month] = selectedYearMonth.split("-");
    const daysInMonth = new Date(year, month, 0).getDate();
    const dates = Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      return `${year}-${month.padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
    });

    const tripCounts = tripData.reduce((counts, trip) => {
      const date = dayjs(trip.created_at).format("YYYY-MM-DD");
      if (dayjs(trip.created_at).format("YYYY-MM") === selectedYearMonth) {
        counts[date] = (counts[date] || 0) + 1;
      }
      return counts;
    }, {});

    const cancelledTrips = tripData.reduce((counts, trip) => {
      if (trip.status === "CANCELLED") {
        const date = dayjs(trip.created_at).format("YYYY-MM-DD");
        if (dayjs(trip.created_at).format("YYYY-MM") === selectedYearMonth) {
          counts[date] = (counts[date] || 0) + 1;
        }
      }
      return counts;
    }, {});

    const completedTrips = tripData.reduce((counts, trip) => {
      if (trip.status === "TRIP_ENDED") {
        const date = dayjs(trip.created_at).format("YYYY-MM-DD");
        if (dayjs(trip.created_at).format("YYYY-MM") === selectedYearMonth) {
          counts[date] = (counts[date] || 0) + 1;
        }
      }
      return counts;
    }, {});

    // Prepare data for the chart
    const chartData = dates.map((date) => ({
      date,
      day: dayjs(date).format("DD"),
      totalTrips: tripCounts[date] || 0,
      cancelledTrips: cancelledTrips[date] || 0,
      completedTrips: completedTrips[date] || 0,
    }));

    setAreaChartData(chartData);
  };

  const handleYearMonthChange = (event) => {
    setSelectedYearMonth(event.target.value);
  };

  const generateYearMonthOptions = () => {
    const options = [];
    const currentYear = dayjs().year();
    for (let year = currentYear - 1; year <= currentYear; year++) {
      for (let month = 0; month < 12; month++) {
        options.push(dayjs().year(year).month(month).format("YYYY-MM"));
      }
    }
    return options;
  };

  return (
    <Paper elevation={1} style={{ width: "100%", overflowX: "auto" }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <CardHeader
            title="Trips"
            titleTypographyProps={{ variant: "h6", sx: { color: "#667085" } }}
            action={
              <FormControl variant="outlined" size="small">
                <InputLabel id="year-month-select-label">Year-Month</InputLabel>
                <Select
                  labelId="year-month-select-label"
                  value={selectedYearMonth}
                  onChange={handleYearMonthChange}
                  label="Year-Month"
                >
                  {generateYearMonthOptions().map((option) => (
                    <MenuItem key={option} value={option}>
                      {dayjs(option).format("MMMM YYYY")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            }
          />
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart
              data={areaChartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorTotalTrips" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorCompletedTrips" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorCancelledTrips" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF0000" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#FF0000" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="totalTrips"
                stroke="#8884d8"
                fill="url(#colorTotalTrips)"
                name="Total Trips"
              />
              <Area
                type="monotone"
                dataKey="completedTrips"
                stroke="#82ca9d"
                fill="url(#colorCompletedTrips)"
                name="Completed Trips"
              />
              <Area
                type="monotone"
                dataKey="cancelledTrips"
                stroke="#FF0000"
                fill="url(#colorCancelledTrips)"
                name="Cancelled Trips"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default TripChart;
